import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Grid } from "@/comn/components";
import { utils, comnUtils, comnEnvs } from "@/comn/utils"; // 프로젝트 공통 유틸
import { Page, Group, Layout, Button, FormControl } from "@/comn/components"; // 화면 구성 컴포넌트
import {
    useFetch,
    useForm,
    useGrid,
    useStore,
    useToast,
    useResource,
    useModal,
    usePage,
    usePopup,
    TFormSchema,
} from "@/comn/hooks";
import {
    APIS,
    SF_USR_APLY_INFO,
    SG_CO_TP_APLY_LIST,
    SF_CO_TP_INFO,
    FIELD_VISIBILITY_MAP,
    ATCH_FILE_VISIBILITY_MAP,
    CO_CD_INPUT_TRGT_SET,
    CO_CD_INPUT_PROPERTIES,
    URLS,
    JNIA_APRV_RQST_CO_TP_CD_SET,
    DSM_APRV_RQST_CO_TP_CD_SET,
    BASIC_APRV_RQST_CO_TP_CD_SET,
    LCNE_APRV_RQST_CO_TP_CD_SET,
    CSC_APRV_RQST_CO_TP_CD_SET,
    TMU_APRV_RQST_CO_TP_CD_SET,
    CO_CD_INPUT_TASAC_SET,
} from "@/tra/tancis/ptli/usr/coaprv/services/PtliCoAprvService";

import { getNameOfJSDocTypedef } from "typescript";

/*
 * Company Approval Request Details
 * !== 업체 승인 요청 상세 ==!
 */
export const PTLI0915002S = (): JSX.Element => {
    const { pgeUid, menuId } = usePage({
        auth: ["auth_ptli0015", "auth_ptlianyADM", "auth_ptli0064"],
    });
    const { t } = useTranslation();
    const navigate = useNavigate();
    const modal = useModal(); // Modal Window Hook !== Modal 창 Hook ==!
    const { openPopup } = usePopup();
    const { pgeStore, setStore, getStore } = useStore({ pgeUid: pgeUid }); // Page Store Hook !== 화면 데이터 저장 Hook ==!
    const toast = useToast();
    const { xtrnUsrAplyId } = useParams(); // Key Information of this Component Router !== 라우터에 정의된 키정보 ==!
    const [rtrnYn, setRtrnYn] = useState("N");
    const [btns, setBtns] = useState({ regist: false, elecAprv: false });
    const [oldCoTpAplyList, setOldCoTpAplyList] = useState([]);
    /*
     * Declaration Hook, Meta
     * !== Hook, 메타 정보 정의 ==!
     */
    useResource({
        defaultSchema: [
            { area: "comnCd", comnCd: "COM0011" },
            { area: "comnCd", comnCd: "CGM0095" },
            { area: "comnCd", comnCd: "CGM0096" },
            { area: "cstmCd" },
            { area: "cntyCd" },
        ],
    });

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        // User Information !== 사용자(대표자) 정보 ==!
        usrAplyInfo: useForm({
            defaultSchema: SF_USR_APLY_INFO,
        }),
        coTpAplyInfo: useForm({
            defaultSchema: SF_CO_TP_INFO,
        }),
    };

    const grid = {
        // Company Type List !== 업체 유형 목록 ==!
        coTpList: useGrid({
            defaultSchema: SG_CO_TP_APLY_LIST,
            page: pgeStore?.page,
            size: pgeStore?.size,
        }),
    };

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        // Get License User Information !== 업체 사용자 정보 조회 ==!
        getCoAprvRqstInfo: useFetch({
            api: () => APIS.getCoAprvRqstInfo(xtrnUsrAplyId),
            enabled: !!xtrnUsrAplyId,
            onSuccess: (data) => {
                grid.coTpList.clearData();

                const coAprvRqstInfo = data.coAprvRqstInfo.content;

                form.usrAplyInfo.setValues(coAprvRqstInfo);

                if (!comnUtils.isEmptyArray(coAprvRqstInfo.coTpList)) {
                    coAprvRqstInfo.coTpList.forEach((item: any) => {
                        grid.coTpList.addRow({ ...item });
                    });
                }

                setBtns((btns: any) => ({ ...btns, regist: coAprvRqstInfo.aprvPrcssStatCdNm === null }));

                form.usrAplyInfo.setEditable("rtrnRsn", coAprvRqstInfo.aprvYn == "N" && coAprvRqstInfo.rtrnRsn == null);

                setRtrnYn(coAprvRqstInfo.rtrnRsn == null ? "N" : "Y");

                setOldCoTpAplyList(coAprvRqstInfo.coTpList);
                form.coTpAplyInfo.clearValues();
            },
            onError: (error) => {},
            showToast: true,
        }),
        //Check Company Type applications List !==  업체 유형 신청 목록 조회 ==!
        getCoTpAplyList: useFetch({
            api: () => APIS.getCoTpAplyList(xtrnUsrAplyId),
            onSuccess: () => {},
            onError: () => toast.showToast({ type: "error", content: "msg.com.00025" }), // Failed to select. !== 조회가 실패하였습니다. ==!
            showToast: true,
        }),
        // Execute Company Approval !== 업체 승인 ==!
        executeCoAprv: useFetch({
            api: (data) => APIS.executeCoAprv({ ...data }),
            onSuccess: () => {
                modal.openModal({ content: "msg.com.00066" }); // Successfully approved., !== 정상적으로 승인되었습니다. ==!
                fetch.getCoAprvRqstInfo.fetch();
            },
            onError: (error) => {
                console.log(error);
                const data = error.response.data;
                if (error.response.status === 400) {
                    form.usrAplyInfo.setErrors(error.response.data.errors);
                }

                modal.openModal({
                    content: data.errorMessage,
                });
            },
        }),
        // Execute Company Return !== 업체 반려 ==!
        executeCoRtrn: useFetch({
            api: (data) => APIS.executeCoRtrn({ ...data }),
            onSuccess: () => {
                modal.openModal({ content: "msg.com.00065" }); // Successfully returned., !== 정상적으로 반려되었습니다. ==!
                fetch.getCoAprvRqstInfo.fetch();
            },
            onError: (error) => {
                console.log(error);
                const data = error.response.data;
                if (error.response.status === 400) {
                    form.usrAplyInfo.setErrors(error.response.data.errors);
                }

                modal.openModal({
                    content: data.errorMessage,
                });
            },
        }),

        updateCoTpInfo: useFetch({
            api: (data) => APIS.updateCoTpInfo({ ...data }),
            onSuccess: () => {
                modal.openModal({ content: "msg.com.00062" }); // Successfully saved., !== 정상적으로 저장되었습니다. ==!
                fetch.getCoAprvRqstInfo.fetch();
            },
            onError: (error) => {
                console.log(error);
                const data = error.response.data;
                if (error.response.status === 400) {
                    form.usrAplyInfo.setErrors(error.response.data.errors);
                }

                modal.openModal({
                    content: data.errorMessage,
                });
            },
        }),
        // Enter the payment ID in the user application information. !== 사용자 신청 정보에 결재아이디를 넣어준다. ==!
        saveUsrAplyId: useFetch({
            api: (data) => APIS.saveUsrAplyId({ ...data }),
            onSuccess: (data) => {
                form.usrAplyInfo.setValue("aprvPrcssStatCdNm", "Requested");
            },
            onError: (error) => {
                if (error.response.status === 400) {
                    form.usrAplyInfo.setErrors(error.response.data.errors);
                }
                modal.openModal({ content: "msg.00006" }); // An error occurred during processing.
            },
        }),
    };

    const render = {
        coTpList: {
            cell: {
                cstmOfceCdd: (props: any) => {
                    const { binding, rowValues, value } = props;

                    const cstmOfceNm = utils.getCodeLabel("cstmCd", value);

                    return `${value}, ${cstmOfceNm}`;
                },
            },
        },
    };

    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        // Company Type List Cell Click  !== 업체 유형 목록 셀 클릭 ==!
        onCoTpListRowClick: (data: any) => {
            if (
                !comnUtils.isEmpty(form.coTpAplyInfo.getValue("coSn")) &&
                FIELD_VISIBILITY_MAP.get(form.coTpAplyInfo.getValue("coTpCd"))
            ) {
                modal.openModal({
                    content: "msg.com.00047", // Confirm save? !== 저장하시겠습니까? ==!
                    onConfirm: () => {
                        handler.onSaveBtnClick();
                    },
                    onCancel: () => {
                        form.coTpAplyInfo.setValues(data);
                        form.coTpAplyInfo.setEditable("bsopId", false);
                    },
                });
            } else {
                form.coTpAplyInfo.setValues(data);
                form.coTpAplyInfo.setEditable("bsopId", false);
            }
        },
        // Execute Company Approval !== 업체 승인 ==!
        executeCoAprv: () => {
            if (!comnUtils.isEmpty(form.usrAplyInfo.errors)) {
                toast.showToast({ type: "warning", content: "msg.00002" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                return;
            }
            form.usrAplyInfo.handleSubmit(
                () => {
                    const data = form.usrAplyInfo.getValues();
                    const coTpAplyInfo = form.coTpAplyInfo.getValues();

                    if (typeof coTpAplyInfo.bsopId === "object") {
                        //coTpAplyInfo.atchFile = coTpAplyInfo.bsopId;
                        coTpAplyInfo.bsopId = null;
                    }

                    modal.openModal({
                        content: "msg.com.00053", // Confirm approve? !== 승인하시겠습니까? ==!
                        onConfirm: () => {
                            fetch.executeCoAprv.fetch({ ...data, coTpAplyInfo: coTpAplyInfo });
                        },
                    });
                },
                () => {
                    toast.showToast({ type: "warning", content: "msg.00002" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                },
            )();
        },
        // Execute Company Return !== 업체 반려 ==!
        executeCoRtrn: () => {
            if (!comnUtils.isEmpty(form.usrAplyInfo.errors)) {
                toast.showToast({ type: "warning", content: "msg.00002" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                return;
            }
            form.usrAplyInfo.handleSubmit(
                () => {
                    const formData = form.usrAplyInfo.getValues();
                    formData.aprvCoYn = aprvCoYn;

                    if (aprvCoYn == "Y") {
                        const handlePopup = (data: any) => {
                            formData.rtrnRsn = data;

                            modal.closeModal();

                            fetch.executeCoRtrn.fetch(formData);
                        };

                        modal.openModal({
                            content: <ModalRtrnRsn handlePopup={handlePopup} />,
                            size: "sm",
                            draggable: true,
                        });
                    } else {
                        formData.rtrnRsn = "Reject";
                        modal.openModal({
                            content: "msg.com.00052", // Confirm return? !== 반려하시겠습니까? ==!
                            onConfirm: () => {
                                fetch.executeCoRtrn.fetch(formData);
                            },
                        });
                    }
                },
                () => {
                    toast.showToast({ type: "warning", content: "msg.00002" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                },
            )();
        },

        // Enter the payment ID in the user application information. !== 사용자 신청 정보에 결재아이디를 넣어준다. ==!
        saveUsrAplyId: () => {
            form.usrAplyInfo.handleSubmit(
                () => {
                    const data = form.usrAplyInfo.getValues();
                    fetch.saveUsrAplyId.fetch(data);
                },
                () => toast.showToast({ type: "warning", content: "msg.00002" }), // Data is invalid, !== 데이터가 유효하지않습니다. ==!
            )();
        },

        // Save Button Click
        onSaveBtnClick: () => {
            if (comnUtils.isEmpty(form.coTpAplyInfo.getValue("coAplyId"))) return;

            if (!comnUtils.isEmpty(form.coTpAplyInfo.errors)) {
                toast.showToast({ type: "warning", content: "msg.00002" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                return;
            }
            form.coTpAplyInfo.handleSubmit(
                () => {
                    const data = form.coTpAplyInfo.getValues();
                    if (typeof data.bsopId === "object") {
                        data.bsopId = null;
                    }

                    fetch.updateCoTpInfo.fetch(data);
                },
                () => {
                    toast.showToast({ type: "warning", content: "msg.00002" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                },
            )();
        },

        // Approve Button Click
        onAprvRqstBtnClick: () => {
            const coTpList = grid.coTpList.getData();
            const coTpSize = coTpList.length;
            if (coTpSize == 0) {
                // !== 업체유형 코드가 없는 경우 ==!
            } else {
                const coTpDivSet = new Set();
                coTpList.forEach((item) => {
                    const coTpCd = item.coTpCd;
                    if (JNIA_APRV_RQST_CO_TP_CD_SET.has(coTpCd)) {
                        coTpDivSet.add("J");
                    }
                    if (DSM_APRV_RQST_CO_TP_CD_SET.has(coTpCd)) {
                        coTpDivSet.add("D");
                    }
                    if (CSC_APRV_RQST_CO_TP_CD_SET.has(coTpCd)) {
                        coTpDivSet.add("C");
                    }
                    if (TMU_APRV_RQST_CO_TP_CD_SET.has(coTpCd)) {
                        coTpDivSet.add("T");
                    }
                    if (LCNE_APRV_RQST_CO_TP_CD_SET.has(coTpCd)) {
                        coTpDivSet.add("L");
                    }
                    if (BASIC_APRV_RQST_CO_TP_CD_SET.has(coTpCd)) {
                        coTpDivSet.add("B");
                    }
                });

                if (coTpDivSet.size == 1) {
                    if (coTpDivSet.has("J")) {
                        form.usrAplyInfo.setValue("authAprvRqstList", [
                            { aprvPrcssOrd: "1", usrDmnId: "auth_ptli0030", cstmSctnOrgzCd: "JNIA" }, // SECTION MANAGER
                            { aprvPrcssOrd: "2", usrDmnId: "auth_ptli0038" }, // CAS OFFICER
                            { aprvPrcssOrd: "3", usrDmnId: "auth_ptli0037" }, // CAS MANAGER
                        ]);
                    } else if (coTpDivSet.has("D")) {
                        form.usrAplyInfo.setValue("authAprvRqstList", [
                            { aprvPrcssOrd: "1", usrDmnId: "auth_ptli0030", cstmSctnOrgzCd: "DSM" }, // SECTION MANAGER
                            { aprvPrcssOrd: "2", usrDmnId: "auth_ptli0038" }, // CAS OFFICER
                            { aprvPrcssOrd: "3", usrDmnId: "auth_ptli0037" }, // CAS MANAGER
                        ]);
                    } else if (coTpDivSet.has("C")) {
                        form.usrAplyInfo.setValue("authAprvRqstList", [
                            { aprvPrcssOrd: "1", usrDmnId: "auth_ptli0030", cstmSctnOrgzCd: "CSC" }, // SECTION MANAGER
                            { aprvPrcssOrd: "2", usrDmnId: "auth_ptli0038" }, // CAS OFFICER
                            { aprvPrcssOrd: "3", usrDmnId: "auth_ptli0037" }, // CAS MANAGER
                        ]);
                    } else if (coTpDivSet.has("T")) {
                        form.usrAplyInfo.setValue("authAprvRqstList", [
                            { aprvPrcssOrd: "1", usrDmnId: "auth_ptli0030", cstmSctnOrgzCd: "TM" }, // SECTION MANAGER
                            { aprvPrcssOrd: "2", usrDmnId: "auth_ptli0038" }, // CAS OFFICER
                            { aprvPrcssOrd: "3", usrDmnId: "auth_ptli0037" }, // CAS MANAGER
                        ]);
                    } else if (coTpDivSet.has("L")) {
                        form.usrAplyInfo.setValue("authAprvRqstList", [
                            { aprvPrcssOrd: "1", usrDmnId: "auth_ptli0025" }, // LICENSE MANAGER
                            { aprvPrcssOrd: "2", usrDmnId: "auth_ptli0038" }, // CAS OFFICER
                            { aprvPrcssOrd: "3", usrDmnId: "auth_ptli0037" }, // CAS MANAGER
                        ]);
                    } else {
                        form.usrAplyInfo.setValue("authAprvRqstList", [
                            { aprvPrcssOrd: "1", usrDmnId: "auth_ptli0038" }, // CAS OFFICER
                            { aprvPrcssOrd: "2", usrDmnId: "auth_ptli0037" }, // CAS MANAGER
                        ]);
                    }
                } else {
                    form.usrAplyInfo.setValue("authAprvRqstList", [
                        { aprvPrcssOrd: "1", usrDmnId: "auth_ptli0038" }, // CAS OFFICER
                        { aprvPrcssOrd: "2", usrDmnId: "auth_ptli0037" }, // CAS MANAGER
                    ]);
                }

                handler.onAprvRqstSubmit();
            }
        },

        onAprvRqstSubmit: () => {
            form.usrAplyInfo.handleSubmit(
                () => {
                    modal.openModal({
                        url: "/comn/comn/ppup/roleAprvRqstDrt",
                        params: {
                            bsopClsfCd: "PTL",
                            aprvReffId: "/" + form.usrAplyInfo.getValue("xtrnUsrAplyId"),
                            scrnDocCd: "scrn_ptla0004",
                            aprvAfPrcssCfgnId: "cfgn_ptl0007",
                            rjctAfPrcssCfgnId: "cfgn_ptl0008",
                            preStageRtrnUseYn: "Y", // 이전단계 반려처리가 필요한 경우에만 'Y'로 넘겨 줄 것 (결재처리 시 이전단계반려 라디오버튼을 보여줌)
                            itsfAprvYn: "", // 업무팀 자체 결재처리 시 'Y'로 넘겨 줄 것 (결재처리탭 비활성화)
                            delYn: "N",
                            rjctStatUseYn: "Y", // 거절상태사용여부
                            //aprvUsrMdfyYn: "N", // 결재사용자변경여부
                            aprvPrcssPathMgmtId: "APRV00000001", // 승인처리패스관리ID
                            aprvRqstTtleNm: "Company approval",
                            authAprvRqstList: form.usrAplyInfo.getValue("authAprvRqstList"),
                            //docNo: "",
                            docNo: form.usrAplyInfo.getValue("coTin"),
                            testCallback: (data: any) => {
                                if (!comnUtils.isEmpty(data.aprvId)) {
                                    setBtns((btns: any) => ({ ...btns, regist: false }));
                                    // Modify application information after setting payment ID !== 결재ID 세팅 후 신청정보 수정 ==!
                                    form.usrAplyInfo.setValue("aprvId", data?.aprvId);
                                    handler.saveUsrAplyId();
                                }
                            },
                        },
                    });
                },
                () => toast.showToast({ type: "warning", content: "msg.00002" }), // Data is invalid, !== 데이터가 유효하지않습니다. ==!
            )();
        },
    };

    useEffect(() => {
        if (menuId === "elecAprv") {
            setBtns((btns: any) => ({ ...btns, elecAprv: true }));
        }
        fetch.getCoAprvRqstInfo.fetch();
        form.usrAplyInfo.setEditable(false);
    }, []);

    const coTpCd = form.coTpAplyInfo.watch("coTpCd");

    const isFieldVisible = (field: string) => {
        const visibleFields = FIELD_VISIBILITY_MAP.get(coTpCd) || new Set();
        return visibleFields.has(field);
    };

    const isAtchFileVisible = (field: string) => {
        const visibleFields = ATCH_FILE_VISIBILITY_MAP.get(coTpCd) || new Set();
        return visibleFields.has(field);
    };

    const isCoCdInputTrgt = () => {
        return CO_CD_INPUT_TRGT_SET.has(coTpCd);
    };
    const isLcneUseTpCdSelectTrgt = () => {
        return "C" === coTpCd;
    };
    const isCoCdInputTasacTrgt = () => CO_CD_INPUT_TASAC_SET.has(coTpCd);

    const isCellGroupVisible = FIELD_VISIBILITY_MAP.get(coTpCd)?.size;

    const aprvCoYn = form.usrAplyInfo.watch("aprvCoYn");
    const aprvId = form.usrAplyInfo.watch("aprvId");

    return (
        <Page>
            <form>
                <Group>
                    <Group.Header>
                        <Group.Title title={"T_CO_INFO"} />
                    </Group.Header>
                    <Group.Body>
                        <Group.Section>
                            <Group.Row>
                                <Group.Control {...form.usrAplyInfo.schema.coTin} />
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.usrAplyInfo.schema.coNm}></Group.Control>
                                <Group.Control {...form.usrAplyInfo.schema.inwdCoYn}></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.usrAplyInfo.schema.rprsTelno}></Group.Control>
                                <Group.Control {...form.usrAplyInfo.schema.rprsFaxNo}></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.usrAplyInfo.schema.rprsEml}></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.usrAplyInfo.schema.coAddr}></Group.Control>
                            </Group.Row>
                            <Group.Row hidden={btns.elecAprv}>
                                <Group.Control {...form.usrAplyInfo.schema.aprvPrcssStatCdNm} edit={false} />
                                <Group.Control {...form.usrAplyInfo.schema.aprvPrcssOpinCn} edit={false} />
                            </Group.Row>
                        </Group.Section>
                    </Group.Body>
                </Group>
                <Group>
                    <Group.Header>
                        <Group.Title title={t("T_RPPN_INFO")} />
                    </Group.Header>
                    <Group.Body>
                        <Group.Section>
                            <Group.Row>
                                <Group.Control {...form.usrAplyInfo.schema.xtrnUsrIdntfNo} />
                                <Group.Control {...form.usrAplyInfo.schema.usrNm}></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.usrAplyInfo.schema.aplcCntyCd}></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.usrAplyInfo.schema.frgnrYn}></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.usrAplyInfo.schema.usrMoblNo}></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.usrAplyInfo.schema.usrEmlAddr}></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.usrAplyInfo.schema.rsdtAddr}></Group.Control>
                            </Group.Row>
                        </Group.Section>
                    </Group.Body>
                </Group>
                <Group>
                    <Group.Header>
                        <Group.Title title={t("T_CO_TP")} />
                    </Group.Header>
                    <Group.Body>
                        {/*                         
                        <Layout direction="row" hidden={aprvYn == "Y"}>
                            <Layout.Right>
                                <Button
                                    role="save"
                                    onClick={() => {
                                        handler.onUpdateCoTpBtn();
                                    }}
                                ></Button>
                            </Layout.Right>
                        </Layout> */}
                        <Group.Section>
                            {/* <Grid {...grid.coTpList.grid} data={fetch.getCoTpAplyList.data?.coTpList} /> */}
                            <Grid
                                {...grid.coTpList.grid}
                                render={render.coTpList}
                                onRowClick={handler.onCoTpListRowClick}
                            />
                        </Group.Section>
                        <Group.Section key={form.coTpAplyInfo.schema.key}>
                            <Group.Row>
                                <Group.Control {...form.coTpAplyInfo.schema.coTpCd} edit={false} />
                                <Group.Control {...form.coTpAplyInfo.schema.cstmOfceCd} edit={false}></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.coTpAplyInfo.schema.coCd} edit={false}></Group.Control>
                                {isLcneUseTpCdSelectTrgt() && (
                                    <Group.Control
                                        {...form.coTpAplyInfo.schema.lcneUseTpCd}
                                        edit={btns.regist && aprvId === null}
                                        required={isLcneUseTpCdSelectTrgt() && btns.regist && aprvId === null}
                                    />
                                )}
                            </Group.Row>
                            {/* Company Type Name 추가 */}
                            <Group.Row>
                                <Group.Control {...form.coTpAplyInfo.schema.coTpNm} edit={false} />
                                {isCoCdInputTasacTrgt() && (
                                    <Group.Control
                                        {...form.coTpAplyInfo.schema.tasacCrtfNoStr}
                                        edit={false}
                                    ></Group.Control>
                                )}
                            </Group.Row>

                            {isCellGroupVisible != null && isCellGroupVisible > 0 && (
                                <Group.Row>
                                    {isFieldVisible("gainCfrmOmsnYn") && (
                                        <Group.Control {...form.coTpAplyInfo.schema.gainCfrmOmsnYn}></Group.Control>
                                    )}
                                    {isFieldVisible("gaotCfrmOmsnYn") && (
                                        <Group.Control {...form.coTpAplyInfo.schema.gaotCfrmOmsnYn}></Group.Control>
                                    )}
                                    {isFieldVisible("stckCretCtraTpCd") && (
                                        <Group.Control {...form.coTpAplyInfo.schema.stckCretCtraTpCd}></Group.Control>
                                    )}
                                    {isFieldVisible("stckClarCtraTpCd") && (
                                        <Group.Control {...form.coTpAplyInfo.schema.stckClarCtraTpCd}></Group.Control>
                                    )}
                                </Group.Row>
                            )}
                            {isCellGroupVisible != null && isCellGroupVisible > 0 && (
                                <Group.Row>
                                    {isFieldVisible("autoCriYn") && (
                                        <Group.Control {...form.coTpAplyInfo.schema.autoCriYn}></Group.Control>
                                    )}
                                    {isFieldVisible("autoCroYn") && (
                                        <Group.Control {...form.coTpAplyInfo.schema.autoCroYn}></Group.Control>
                                    )}
                                    {isFieldVisible("wrhsDpstDays") && (
                                        <Group.Control {...form.coTpAplyInfo.schema.wrhsDpstDays}></Group.Control>
                                    )}
                                    {isFieldVisible("psnWrhsYn") && (
                                        <Group.Control {...form.coTpAplyInfo.schema.psnWrhsYn}></Group.Control>
                                    )}
                                    {isFieldVisible("spcdlWrhsYn") && (
                                        <Group.Control {...form.coTpAplyInfo.schema.spcdlWrhsYn}></Group.Control>
                                    )}
                                    {isFieldVisible("rskCagYn") && (
                                        <Group.Control {...form.coTpAplyInfo.schema.rskCagYn}></Group.Control>
                                    )}
                                </Group.Row>
                            )}
                            {isAtchFileVisible("bsopId") && (
                                <Group.Row>
                                    <Group.Control {...form.coTpAplyInfo.schema.bsopId} controlSize={10} />
                                </Group.Row>
                            )}
                        </Group.Section>
                    </Group.Body>
                </Group>
            </form>
            <Layout direction="row">
                {!btns.elecAprv && (
                    <Layout.Left>
                        <Button
                            role="list"
                            onClick={() => {
                                navigate(URLS.ptli0915001q);
                            }}
                        ></Button>
                    </Layout.Left>
                )}

                {btns.regist && aprvId === null && (
                    <Layout.Right>
                        <Layout.Button
                            role={"save"}
                            onClick={() => {
                                modal.openModal({
                                    content: "msg.com.00047", // Confirm save? !== 저장하시겠습니까? ==!
                                    onConfirm: () => {
                                        handler.onSaveBtnClick();
                                    },
                                });
                            }}
                        ></Layout.Button>
                        <Layout.Button
                            role={"apply"}
                            onClick={() => {
                                handler.onAprvRqstBtnClick();
                            }}
                        >
                            {t("B_RQST_APRV")}
                        </Layout.Button>
                        <Layout.Button
                            role={"delete"}
                            onClick={() => {
                                handler.executeCoRtrn();
                            }}
                        >
                            {aprvCoYn === "Y" ? t("B_RTRN") : t("B_RJCT")}
                        </Layout.Button>
                    </Layout.Right>
                )}
            </Layout>
        </Page>
    );
};

/*
 * !== 반려사유 Modal ==!
 */
export const ModalRtrnRsn = (props: any) => {
    useResource({
        defaultSchema: [{ area: "bsopClsfCd" }, { area: "comnCd", comnCd: "COM0024" }],
    });

    const { data, handlePopup } = props;

    // 현재 화면설계 참고 -> 항목과 검색어 입력폼 명은 개발에 맞게 변경 해주세요(현재는 라벨이없어서 임시로 넣어놓았습니다)
    const SG_FORM: TFormSchema = {
        id: "form",
        schema: {
            rtrnRsn: { type: "textarea", label: "L_RTRN_RSN", required: true, controlSize: 10, maxLength: 2000 },
        },
    };

    const form = useForm({
        defaultSchema: SG_FORM,
        defaultValues: { ...data },
    });

    const handler = {
        submitBtnClick: () => {
            handlePopup(form.getValue("rtrnRsn"));
        },
    };

    return (
        <>
            <Page>
                <Group>
                    <Group.Body>
                        <Group.Section>
                            <Group.Row>
                                <Group.Control {...form.schema.rtrnRsn} />
                            </Group.Row>
                        </Group.Section>
                        <Group.Section>
                            <Layout>
                                <Layout.Right>
                                    <Button
                                        role={"submit"}
                                        onClick={() => {
                                            handler.submitBtnClick();
                                        }}
                                    />
                                </Layout.Right>
                            </Layout>
                        </Group.Section>
                    </Group.Body>
                </Group>
            </Page>
        </>
    );
};
